import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FaYoutube, FaInstagram, FaTiktok, FaBars, FaTimes } from 'react-icons/fa';
import DarsLogo from '../../DarsLogoTr.png';
import DonateButton from '../DonateButton';
import { CONTACT_EMAIL, SOCIAL_LINKS } from '../../constants';

const NAV_LINKS = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About Us' },
  { to: '/programs', label: 'Our Programs' },
  { to: '/impact', label: 'Impact' },
  { to: '/events', label: 'Events' },
  { to: '/blog', label: 'Blog' },
  { to: '/volunteer', label: 'Volunteer' },
];

const SOCIAL_ICONS = [
  { href: SOCIAL_LINKS.youtube, label: 'YouTube', Icon: FaYoutube },
  { href: SOCIAL_LINKS.instagram, label: 'Instagram', Icon: FaInstagram },
  { href: SOCIAL_LINKS.tiktok, label: 'TikTok', Icon: FaTiktok },
];

const navLinkClass = ({ isActive }) =>
  `px-3 py-2 min-h-[44px] inline-flex items-center text-fluid-sm font-medium transition-all duration-300 ease-in-out rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-canvas ${
    isActive
      ? 'text-gold border-b-2 border-gold font-semibold'
      : 'text-forest/90 hover:text-gold hover:-translate-y-0.5'
  }`;

const mobileNavLinkClass = ({ isActive }) =>
  `px-4 py-3 min-h-[44px] flex items-center text-fluid-base font-medium transition-all duration-300 ${
    isActive ? 'text-gold font-semibold' : 'text-forest/90 hover:text-gold'
  }`;

function HeaderSocialIcons({ className = '' }) {
  return (
    <div className={`hidden xl:flex items-center gap-1 ${className}`}>
      {SOCIAL_ICONS.map(({ href, label, Icon }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-forest/80 hover:text-gold transition-colors duration-300"
        >
          <Icon className="w-5 h-5" aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}

export default function SiteHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-gold focus:text-forest focus:rounded-lg focus:font-bold"
      >
        Skip to main content
      </a>
      <header
        className="fixed top-0 left-0 right-0 z-50 h-16 border-b border-forest/10 bg-canvas overflow-visible"
        role="banner"
      >
        <nav className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-full" aria-label="Main navigation">
          <div className="flex items-center justify-between h-full gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex-shrink-0 h-12 w-[108px] sm:h-14 sm:w-[132px] xl:w-[152px] self-center overflow-hidden rounded flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              aria-label="Dars Initiative home"
            >
              <img
                src={DarsLogo}
                alt="Dars Initiative"
                className="h-12 sm:h-14 w-auto max-w-full object-contain object-left sm:object-top sm:-translate-y-2"
                width={152}
                height={56}
              />
            </button>

            <div className="hidden xl:flex flex-1 items-center justify-center gap-0.5 flex-wrap min-w-0">
              {NAV_LINKS.map((link) => (
                <NavLink key={link.to} to={link.to} end={link.end} className={navLinkClass}>
                  {link.label}
                </NavLink>
              ))}
            </div>

            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <HeaderSocialIcons />

              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="hidden xl:inline-flex min-h-[44px] items-center px-4 py-2 rounded-btn border border-forest/30 text-forest text-btn font-semibold hover:bg-forest/5 transition-all duration-300 ease-in-out hover:-translate-y-0.5"
              >
                Contact
              </a>

              <DonateButton className="shrink-0 !px-3 sm:!px-5 xl:!px-7" />

              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="xl:hidden flex h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg border border-forest/25 bg-canvas text-forest hover:text-gold hover:border-gold/50 hover:bg-forest/5 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav-panel"
              >
                {mobileMenuOpen ? (
                  <FaTimes className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <FaBars className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </nav>
      </header>

      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[45] xl:hidden pt-16"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile menu"
        >
          <button
            type="button"
            className="absolute inset-0 bg-forest/40"
            aria-label="Close menu"
            onClick={closeMenu}
          />
          <div
            id="mobile-nav-panel"
            className="relative bg-canvas shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-forest/10"
          >
            <div className="flex flex-col py-2">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                onClick={closeMenu}
                className="px-4 py-3 min-h-[44px] flex items-center text-fluid-base font-medium text-forest hover:text-gold border-b border-forest/10"
              >
                Contact
              </a>
              <div className="flex gap-2 px-4 py-3 border-b border-forest/10">
                {SOCIAL_ICONS.map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg text-forest hover:text-gold"
                  >
                    <Icon className="w-6 h-6" aria-hidden="true" />
                  </a>
                ))}
              </div>
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={closeMenu}
                  className={mobileNavLinkClass}
                  end={link.end}
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="h-16" aria-hidden="true" />
    </>
  );
}
